import { useCallback, useEffect, useState } from 'react'

type Position = 'left' | 'right' | 'top' | 'bottom'

interface UseAnchoredPositionOptions {
	anchor: string | null // CSS selector
	position: Position
	offset?: number
	fallback?: Position
}

interface Coords {
	top: number
	left: number
}

export function useAnchoredPosition({ anchor, position, offset = 12, fallback }: UseAnchoredPositionOptions) {
	const [coords, setCoords] = useState<Coords | null>(null)
	const [actualPosition, setActualPosition] = useState<Position>(position)

	const calculate = useCallback(() => {
		if (!anchor) {
			setCoords(null)
			return
		}

		const element = document.querySelector(anchor)
		if (!element) {
			setCoords(null)
			return
		}

		const rect = element.getBoundingClientRect()
		const popoverWidth = 320
		const popoverHeight = 200

		let pos = position
		let top = 0
		let left = 0

		// Check if position would overflow viewport
		const wouldOverflow = {
			right: rect.right + offset + popoverWidth > window.innerWidth,
			left: rect.left - offset - popoverWidth < 0,
			bottom: rect.bottom + offset + popoverHeight > window.innerHeight,
			top: rect.top - offset - popoverHeight < 0,
		}

		if (wouldOverflow[position] && fallback && !wouldOverflow[fallback]) {
			pos = fallback
		}

		switch (pos) {
			case 'right':
				top = rect.top + rect.height / 2
				left = rect.right + offset
				break
			case 'left':
				top = rect.top + rect.height / 2
				left = rect.left - offset - popoverWidth
				break
			case 'bottom':
				top = rect.bottom + offset
				left = rect.left + rect.width / 2 - popoverWidth / 2
				break
			case 'top':
				top = rect.top - offset - popoverHeight
				left = rect.left + rect.width / 2 - popoverWidth / 2
				break
		}

		setCoords({ top, left })
		setActualPosition(pos)
	}, [anchor, position, offset, fallback])

	useEffect(() => {
		calculate()
		window.addEventListener('resize', calculate)
		window.addEventListener('scroll', calculate, true)
		return () => {
			window.removeEventListener('resize', calculate)
			window.removeEventListener('scroll', calculate, true)
		}
	}, [calculate])

	return { coords, actualPosition, recalculate: calculate }
}
